import { IconButton, SelectChangeEvent } from '@mui/material';
import { CustomerFilters } from './CustomerFilters';
import { ReactNode, useEffect, useState } from 'react';
import { EditableCustomer, EditCustomer } from '../modals/EditCustomer';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BkTable from 'src/components/table/BkTable';
import { AppDispatch, RootState } from 'src/store/store';
import { useDispatch, useSelector } from 'react-redux';
import { deleteOrganization, editCustomer, getCustomers, getDocSheets } from 'src/features/admin/utils';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import { updateCustomers } from 'src/features/admin/adminSlice';

interface CustomerFilters {
  type: string[];
  priceSheet: string[];
  searchValue: string;
}

const columns = [
  { headerName: 'Organization Name', field: 'name' },
  { headerName: 'Domains', field: 'domains' },
  { headerName: 'Org Type', field: 'type' },
  { headerName: 'Price sheet', field: 'priceSheet' },
  { headerName: 'Print credit', field: 'printCredit', alignSelf: 'right', headerCustomStyles: { textAlign: 'right ' } },
  { headerName: 'Action', field: 'action', alignSelf: 'center' },
] as { headerName: string; field: string; alignSelft?: string }[];

interface TableData {
  id: number;
  name: string;
  domains: ReactNode;
  type: string;
  priceSheet: string;
  printCredit: string;
  action: ReactNode;
}
interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

const Customers: React.FC = () => {
  const [selectedFilters, setSelectedFilters] = useState<CustomerFilters>({
    type: [],
    priceSheet: [],
    searchValue: '',
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const dispatch: AppDispatch = useDispatch();

  const [selectedOrganization, setSelectedOrganization] = useState<any>();
  const [data, setData] = useState<TableData[]>([]);
  const [searchTerm, setSearchTerm] = useState(selectedFilters.searchValue);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });

  const isFetchingCustomers = useSelector((state: RootState) => state.admin.isFetchingOrganizations);
  const fetchCustomersError = useSelector((state: RootState) => state.admin.fetchOrganizationsError);
  const customers = useSelector((state: RootState) => state.admin.customers);
  const priceSheets = useSelector((state: RootState) => state.admin.docSheets);
  useEffect(() => {
    dispatch(getDocSheets());
  }, []);

  useEffect(() => {
    const skip = page * rowsPerPage;
    const take = rowsPerPage;

    dispatch(getCustomers({ filters: selectedFilters, skip, take })).then((response) => {
      setTotalCount(response.payload.totalCount);
    });
  }, [selectedFilters, page, rowsPerPage]);

  useEffect(() => {
    const data: TableData[] = [];
    customers.forEach((customer) => {
      data.push({
        id: customer.id,
        name: customer.name,
        domains: customer.domains.map((domain) => domain.name).join(', '),
        type: customer.type,
        priceSheet:
          Number(customer.sheetId) === 1
            ? 'Default'
            : Number(customer.sheetId) === 2
              ? 'Chapter 13 Trustee'
              : customer.sheet.name,
        printCredit: `$${customer.printCredits}`,
        action: renderAction(customer.id),
      });
    });
    setData(data);
  }, [customers]);

  const renderAction = (organizationId: number) => {
    const onEditClick = () => {
      setIsModalOpen(true);
      const organization = customers.find((organization) => organization.id === organizationId);
      setSelectedOrganization({
        id: organization?.id,
        name: organization?.name,
        domains: organization?.domains,
        type: organization?.type,
        customOrgType: '',
        sheetId: organization?.sheetId,
        printCredits: organization?.printCredits,
      });
    };

    return (
      <IconButton onClick={onEditClick}>
        <EditOutlinedIcon />
      </IconButton>
    );
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        searchValue: searchTerm,
      }));
      setPage(0)
    }, 1000);

    return () => clearTimeout(handler);
  }, [searchTerm]); // debounce search value

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event: SelectChangeEvent<string | (string | null)[]>) => {
    setSelectedFilters((prev) => ({ ...prev, [event.target.name]: event.target.value }));
    setPage(0);
  };

  const onEditCustomer = (info: EditableCustomer) => {
    const stringDomains = info.domains.map((domain) => domain.name);
    const idDomains = info.domains.map((domain) => domain.id).filter(Number);

    const { customOrgType, ...infoWithoutCustomOrgType } = info;
    const formattedCustomerInfo = {
      ...infoWithoutCustomOrgType,
      sheetId: Number(info.sheetId),
      domains: {
        domainNames: stringDomains.filter(
          (domain) => !selectedOrganization.domains.some((d: any) => d.name === domain),
        ),
        deleteIds: selectedOrganization.domains
          .filter((domain: any) => !idDomains.includes(domain.id))
          .map((item: any) => item.id),
      },
    };

    dispatch(editCustomer({ ...formattedCustomerInfo, id: selectedOrganization.id })).then((result) => {
      const editedCustomer = {
        ...info,
        domains: [...info.domains.filter((d) => typeof d.id === 'number'), ...(result.payload.data?.domains ?? [])],
      };
      const updatedCustomers = customers.map((customer) =>
        customer.id === selectedOrganization.id
          ? {
              ...customer,
              ...editedCustomer,
              sheet: priceSheets.find((sheet) => sheet.id === Number(editedCustomer.sheetId)),
            }
          : customer,
      );
      if (editCustomer.fulfilled.match(result)) {
        dispatch(updateCustomers(updatedCustomers));
        setShowSnackbarMsg({
          msg: 'Successfully updated the customer!',
          type: 'success',
        });
        setIsModalOpen(false);
      }
      if (editCustomer.rejected.match(result)) {
        setShowSnackbarMsg({
          msg: 'Something went wrong!',
          type: 'error',
        });
      }
    });
  };

  const onDeleteOrganization = () => {
    dispatch(deleteOrganization(selectedOrganization.id)).then((result) => {
      if (deleteOrganization.fulfilled.match(result)) {
        const newCustomers = customers.filter((customer) => customer.id !== selectedOrganization.id);
        dispatch(updateCustomers(newCustomers));
        setShowSnackbarMsg({
          msg: 'Successfully deleted customer!',
          type: 'success',
        });
      }
      if (deleteOrganization.rejected.match(result)) {
        setShowSnackbarMsg({
          msg: 'Something went wrong!',
          type: 'error',
        });
      }
    });
    setIsModalOpen(false);
  };

  return (
    <>
      <CustomerFilters
        sheets={priceSheets.map((sheet) => ({
          id: sheet.id.toString(),
          label: sheet.id === 1 ? 'Default' : sheet.id === 2 ? 'Chapter 13 Trustee' : sheet.name,
        }))}
        searchTerm={searchTerm}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        handleSearchChange={handleSearchChange}
      />
      {data.length === 0 && isFetchingCustomers === false ? (
        fetchCustomersError ? (
          <p>Something went wrong...</p>
        ) : (
          <p>No customer matches this criteria.</p>
        )
      ) : (
        <BkTable
          page={page}
          onRowsPerPageChange={setRowsPerPage}
          onPageChange={setPage}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          data={data}
          columns={columns}
          initialRowsPerPage={10}
        />
      )}
      <EditCustomer
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onSubmit={onEditCustomer}
        onDelete={onDeleteOrganization}
        info={selectedOrganization}
        sheets={priceSheets.map((sheet) => ({
          id: sheet.id.toString(),
          label: sheet.id === 1 ? 'Default' : sheet.id === 2 ? 'Chapter 13 Trustee' : sheet.name,
        }))}
      />
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
    </>
  );
};

export default Customers;
